import React, { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import { DateTime } from 'luxon';
import FileSaver from 'file-saver';
import { QuipliLink } from '../../../components/link/quipli-link';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { Spinner } from '../../../components/spinner/spinner';
import { Paginator } from '../../../components/paginator/paginator-v2';
import { RenterReportRequest, RenterReportRow } from '../../../clients/quipli-service/report/report';
import { displayMoneyFormat } from '../../../util/util';
import { useSearchParams } from 'react-router-dom';
import { SearchBar } from '../../../components/search-bar/search-bar';
import classNames from 'classnames';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { SelectForm } from '../../../components/select-form/select-form';
import { SortIcon } from '../../../components/table/desktop-data-table';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Badge } from '../../../components/badge/badge';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

function extractRenterReportSearchParams(params: URLSearchParams): RenterReportRequest {
  return {
    keyword: params.get('keyword') || undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
    sortBy: params.get('sortBy') || undefined,
    sortDirection: params.get('sortDirection') || undefined,
    from: params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined,
    to: params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined,
  } as RenterReportRequest;
}

export const UsersReport = () => {
  const { rentersReport, exportRentersReport } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();
  const [currency, setCurrency] = useState<string>();
  const [timezone, setTimezone] = useState<string>();
  const [renterReport, setRenterReport] = useState<Pagination<RenterReportRow>>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [daysPastDue, setDaysPastDue] = useState<number>();
  const [creditLimitEnabled, setCreditLimitEnabled] = useState<boolean>(false);
  const [filtersModal, setFiltersModal] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<DateTime | undefined>();
  const [startDate, setStartDate] = useState<DateTime | undefined>();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const daysPastDueOptions = [
    { label: 'Current', value: 1 },
    { label: '30 days', value: 30 },
    { label: '60 days', value: 60 },
    { label: '90 days', value: 90 },
    { label: '120 days', value: 120 },
  ];
  // List searchRequest hooks
  const [params] = useSearchParams();
  const {
    request: reportRequest,
    setRequest: setReportRequest,
    numOfParams,
  } = useRequestQueryParams<RenterReportRequest>(
    extractRenterReportSearchParams(params),
    async () => {
      setIsLoading(true);
      const report = await rentersReport(reportRequest);
      setRenterReport(report);
      setIsLoading(false);
    },
    [currency],
  );

  // Hooks
  useEffect(() => {
    const init = async () => {
      const confs = await getMultipleConfigurations(['CONF_CURRENCY', 'CONF_CREDIT_LIMIT', 'CONF_TIMEZONE']);
      const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
      const _timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
      const creditLimit = confs.find((c) => c.variable === 'CONF_CREDIT_LIMIT')?.value === '1';
      setCurrency(currency || 'USD');
      setCreditLimitEnabled(creditLimit);
      if (_timezone) {
        setTimezone(_timezone);
      }
      setEndDate(params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined);
      setStartDate(params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined);
      setDaysPastDue(params.has('daysPastDue') ? parseInt(params.get('daysPastDue')!) : undefined);
    };
    init();
  }, []);

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const file = await exportRentersReport({ ...reportRequest, pageNumber: undefined, pageSize: undefined });
      const exportFileName = `Users_Report_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';
      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      console.error(e);
      toast('Failed to export report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  function resort(sortBy: 'Due' | '# of Days Past Due' | 'Total Due' | 'Lifetime Total') {
    setReportRequest((req) => {
      let sortDirection: 'asc' | 'desc';
      if (sortBy === req.sortBy && req.sortDirection === 'desc') {
        sortDirection = 'asc';
      } else {
        sortDirection = 'desc';
      }

      return { ...req, sortBy, sortDirection };
    });
  }

  function resetSearch() {
    setStartDate(undefined);
    setEndDate(undefined);
    setDaysPastDue(undefined);
    setReportRequest((req) => {
      return { ...req, keyword: undefined, daysPastDue: undefined, from: undefined, to: undefined };
    });
  }

  return (
    <div className="payments-report-v2 flex flex-column gap-4">
      <div className="flex justify-between items-center">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Renters Report
        </Typography>
      </div>

      <div className="payments-report-v2 flex w-full flex-wrap justify-between">
        <div className={'flex flex-row gap-2'}>
          <SearchBar
            data-cy="search-renters"
            placeholder="Search by keyword"
            defaultValue={reportRequest.keyword}
            setRequest={setReportRequest}
          />
          <Button
            className="button-secondary !border-0 h-[46px] relative"
            onClick={() => setFiltersModal(true)}
            data-cy="filter-renters"
          >
            <Typography
              type="text-md"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
            </Typography>
            {numOfParams > 0 && (
              <Badge className="absolute -right-2 -top-2" text={`${numOfParams}`} size="small" type="yellow" />
            )}
          </Button>
          {numOfParams > 0 && (
            <Button
              className="button-secondary !border-0 h-[46px]"
              onClick={() => resetSearch()}
              data-cy="clear-filter-order"
            >
              <Typography
                type="text-md"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Clear Filters
              </Typography>
            </Button>
          )}
        </div>
        <Button
          className="self-start lg:self-end	button-yellow !border-0 w-[114px] h-[40px]"
          onClick={() => exportReport()}
          data-cy="export"
        >
          <Typography
            type="text-md"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
          >
            Export
          </Typography>
          <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
        </Button>
      </div>
      <div className="bg-white rounded-[12px]">
        <div className="data-table overflow-x-auto overflow-y-visible bg-white">
          <table>
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-left">Phone Number</th>
                <th className="text-left">Billing Address</th>
                <th className="text-left">Delivery Address</th>
                <th className="text-left">Email</th>
                <th className="text-left">First Rental Date</th>
                <th className="text-left">Orders Placed</th>
                <th className="text-left">Status</th>
                <th className="text-right">
                  <span
                    className={classNames('flex justify-end pr-2 gap-1 whitespace-nowrap', {
                      sortable: 'cursor-pointer',
                    })}
                    onClick={() => resort('# of Days Past Due')}
                  >
                    # of Days Past Due
                    <SortIcon
                      currentDirection={
                        reportRequest.sortBy === '# of Days Past Due'
                          ? reportRequest.sortDirection || 'desc'
                          : undefined
                      }
                    />
                  </span>
                </th>
                <th className="text-right">
                  <span
                    className={classNames('flex justify-end pr-2 gap-1 whitespace-nowrap', {
                      sortable: 'cursor-pointer',
                    })}
                    onClick={() => resort('Due')}
                  >
                    Due
                    <SortIcon
                      currentDirection={
                        reportRequest.sortBy === 'Due' ? reportRequest.sortDirection || 'desc' : undefined
                      }
                    />
                  </span>
                </th>
                <th className="text-right">
                  <span
                    className={classNames('flex justify-end pr-2 gap-1 whitespace-nowrap', {
                      sortable: 'cursor-pointer',
                    })}
                    onClick={() => resort('Total Due')}
                  >
                    Total Due
                    <SortIcon
                      currentDirection={
                        reportRequest.sortBy === 'Total Due' ? reportRequest.sortDirection || 'desc' : undefined
                      }
                    />
                  </span>
                </th>
                <th className="text-right">
                  <span
                    className={classNames('flex justify-end pr-2 gap-1 whitespace-nowrap', {
                      sortable: 'cursor-pointer',
                    })}
                    onClick={() => resort('Lifetime Total')}
                  >
                    Lifetime Total
                    <SortIcon
                      currentDirection={
                        reportRequest.sortBy === 'Lifetime Total' ? reportRequest.sortDirection || 'desc' : undefined
                      }
                    />
                  </span>
                </th>
                {creditLimitEnabled && <th className="text-left">Credit Limit</th>}
                {creditLimitEnabled && <th className="text-left">Credit Utilization</th>}
                <th className="text-left">Date Added</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={100}>
                    <Spinner />
                  </td>
                </tr>
              ) : (
                <>
                  {currency && renterReport && (
                    <>
                      {renterReport.items.length > 0 ? (
                        renterReport.items.map((report, i) => (
                          <TableRow
                            key={i}
                            report={report}
                            currency={currency}
                            creditLimitEnabled={creditLimitEnabled}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={100}>No record found</td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <Paginator
          pagination={renterReport?.pagination}
          request={reportRequest}
          setRequest={(pag) =>
            setReportRequest((req) => {
              return { ...req, ...pag };
            })
          }
        />
      </div>
      <FiltersModal
        isOpen={filtersModal}
        title="Filters"
        onConfirm={() => {
          setFiltersModal(false);
          setReportRequest((prev) => {
            return { ...prev, ...{ from: startDate, to: endDate, daysPastDue } };
          });
        }}
        onCancel={() => {
          setFiltersModal(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              Days Past Due
            </Typography>
            <SelectForm
              options={daysPastDueOptions}
              onChange={(c) => {
                setDaysPastDue(c?.value);
              }}
              getOptionLabel={(c) => c.label}
              getOptionValue={(c) => `${c.value}`}
              isClearable={true}
              placeholder="# of Days Past Due"
              value={daysPastDueOptions.find((c) => c.value === daysPastDue)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={startDate}
                onChange={(d) => {
                  setStartDate(d?.setZone(timezone, { keepLocalTime: true }).startOf('day'));
                }}
                clearable={true}
              />
              <DatePicker
                value={endDate}
                onChange={(d) => setEndDate(d?.setZone(timezone, { keepLocalTime: true }).endOf('day'))}
                clearable={true}
              />
            </div>
          </div>
        </div>
      </FiltersModal>
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Renter Report'} />
    </div>
  );
};

const TableRow = ({
  key,
  report,
  currency,
  creditLimitEnabled,
}: {
  key: number;
  report: RenterReportRow;
  currency: string;
  creditLimitEnabled: boolean;
}) => {
  return (
    <tr key={key}>
      <td>
        <QuipliLink
          className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          to={`users/customer_form/${report['Customer ID']}`}
        >
          {report['Name']}
        </QuipliLink>
      </td>
      <td className="text-left">{report['Phone Number']}</td>
      <td className="text-left">{formatAddress(report['Billing Address'])}</td>
      <td className="text-left">{formatAddress(report['Delivery Address'])}</td>
      <td className="text-left">{report['Email']}</td>
      <td className="text-left">{report['First Rental Date']}</td>
      <td className="text-left">{report['Orders Placed']}</td>
      <td className="text-left">{report['Status']}</td>
      <td className="text-right">{report['# of Days Past Due']}</td>
      <td className="text-right">{displayMoneyFormat(report['Due'], currency)}</td>
      <td className="text-right">{displayMoneyFormat(report['Total Due'], currency)}</td>
      <td className="text-right">{displayMoneyFormat(report['Lifetime Total'], currency)}</td>
      {creditLimitEnabled && <td className="text-right">{displayMoneyFormat(report['Credit Limit'], currency)}</td>}
      {creditLimitEnabled && <td className="text-right">{report['Credit Utilization']}%</td>}
      <td className="text-left Date">
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          {DateTime.fromFormat(report['Date Added'], 'L/d/y h:mm a').toFormat('LLL-dd')}
        </Typography>
        <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
          {DateTime.fromFormat(report['Date Added'], 'L/d/y h:mm a').toFormat('h:mm a')}
        </Typography>
      </td>
    </tr>
  );
};

function formatAddress(addresses: string) {
  const first = addresses.split(';')[0];
  const firstComma = first.indexOf(',');

  return (
    <>
      <div>{first.substring(0, firstComma)}</div>
      <div>{first.substring(firstComma + 1)}</div>
    </>
  );
}
